import { get } from 'lodash'
import { type NextPageWithLayout } from 'next'
import { useRouter } from 'next/router'
import React, { useRef, useEffect } from 'react'
import { EmptyCart } from '@public/components/EmptyCart'
import { getPublicLayout } from '@public/components/PublicLayout'
import { createCheckoutAndCartErrorHandler } from '@public/Showroom/utils/discountErrorHandling'
import { Toast } from 'components/Toast'
import { PAGE_MIN_HEIGHT, WrappedLoader } from 'components/WrappedLoader'
import { ROUTE } from 'constants/routes'
import { useCart } from 'providers/cart'
import {
  GA4_ECOM_CUSTOM_EVENTS,
  GA4_EVENTS,
  useGoogleAnalytics,
} from 'providers/googleAnalytics'
import { useUserAuth } from 'providers/userAuth'
import { prepareGetStaticNavigationProps } from 'routes/utils/prepareGetStaticNavigationProps'
import {
  useCalculatePriceCheckoutQuery,
  useRemoveCouponMutation,
  useRemovePromoCodeMutation,
} from 'types/graphql-generated'
import { CartView } from './CartView'

export const Cart: NextPageWithLayout = () => {
  const { cart, refetchCart, isCartLoading, setItem, removeItem } = useCart()
  const { isNoneSignedIn } = useUserAuth()
  const router = useRouter()
  const isPageTracked = useRef(false)
  const { executeDataToDataLayer } = useGoogleAnalytics()
  const isCartEmpty = get(cart, ['items', 'length'], 0) === 0

  const [removePromoCodeMutation] = useRemovePromoCodeMutation({
    onCompleted: (data) => {
      if (data?.removePromoCode) {
        refetchCart()
      }
    },
  })

  const [removeCouponMutation] = useRemoveCouponMutation({
    onCompleted: (data) => {
      if (data?.removeCoupon) {
        refetchCart()
      }
    },
  })

  const {
    data: calculatedCheckoutPricesData,
    loading: isPriceCalculationLoading,
    refetch: refetchCalculatePrice,
  } = useCalculatePriceCheckoutQuery({
    skip: isCartLoading || isCartEmpty,
    variables: {
      input: {
        email: get(cart, 'email'),
        promoCode: get(cart, ['promoCode', 'code']),
        checkoutItems: [
          {
            quantity: 1,
            cart: {
              cartAvailable: true,
              cartShipmentIds: [],
            },
          },
        ],
      },
    },
    onError: createCheckoutAndCartErrorHandler(
      cart,
      removePromoCodeMutation,
      removeCouponMutation
    ),
  })

  useEffect(() => {
    if (!isCartLoading && !isPageTracked.current) {
      executeDataToDataLayer({
        event: GA4_EVENTS.VIEW_CART,
        overrideData: {
          cart,
        },
      })

      executeDataToDataLayer({
        event: GA4_ECOM_CUSTOM_EVENTS.VIEW_CART_SHOWROOM,
        includeStoredData: false,
      })
      isPageTracked.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCartLoading])

  useEffect(() => {
    // Recalculate price after merging cart
    if (
      !isNoneSignedIn &&
      !isCartLoading &&
      get(cart, ['items', 'length'], 0) > 0
    ) {
      refetchCalculatePrice()
    }
  }, [isNoneSignedIn, cart, isCartLoading, refetchCalculatePrice])

  if (isCartLoading) {
    return <WrappedLoader minHeight={PAGE_MIN_HEIGHT} />
  }

  if (isCartEmpty) {
    return (
      <EmptyCart
        isNoneSignedIn={isNoneSignedIn}
        onViewShowroom={() => {
          router.push(ROUTE.SHOWROOM())
        }}
      />
    )
  }

  const onRemoveCartItem = async (cartItemId: number) => {
    const response = await removeItem(cartItemId)
    if (!response.errors) {
      Toast.dark('Item removed from cart')
    }
    return response
  }

  const checkoutPrices = calculatedCheckoutPricesData?.calculatePriceCheckout

  return (
    <CartView
      cart={cart}
      totalPrice={checkoutPrices?.priceToPayWithoutShipping}
      retailSubtotalPrice={checkoutPrices?.retailSubtotalPrice}
      tradeSubtotalPrice={checkoutPrices?.tradeSubtotalPrice}
      isPriceCalculationLoading={isPriceCalculationLoading}
      onSetCartItem={async (item) => {
        await setItem(item)
        return refetchCalculatePrice()
      }}
      onRemoveCartItem={onRemoveCartItem}
      discountItems={checkoutPrices?.discountItems}
      discount={checkoutPrices?.totalDiscount?.total}
    />
  )
}

export function getStaticProps() {
  return prepareGetStaticNavigationProps()
}

Cart.getLayout = getPublicLayout
